import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { campanhasQueries, convitesQueries, painelQueries, sincronizacaoQueries } from '../queries';
import { painelService } from '../services/painelService';
import type { AcaoLote, FiltroLote, FiltrosPainel, Idioma, SelecaoLote } from '../types/Painel';
import { IDIOMAS } from './useFiltrosParticipantes';

export function usePainel(id: string, filtros: FiltrosPainel) {
  return useQuery(painelQueries.lista(id, filtros));
}

export function useDetalheParticipante(id: string, participanteId: string | null) {
  return useQuery({
    ...painelQueries.detalhe(id, participanteId ?? ''),
    enabled: !!participanteId,
  });
}

/** Retenta a gravação no Axiis do participante e invalida o detalhe e a lista. */
export function useRetentarGravacao(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (participanteId: string) => painelService.retentar(id, participanteId),
    onSettled: () => qc.invalidateQueries({ queryKey: painelQueries.all(id) }),
  });
}

/** Lista, totais, detalhe e o resumo da sincronização (totais) ficam defasados depois de qualquer mudança. */
function useInvalidarPainel(id: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: painelQueries.all(id) }),
      qc.invalidateQueries({ queryKey: sincronizacaoQueries.all(id) }),
      qc.invalidateQueries({ queryKey: campanhasQueries.progresso(id).queryKey }),
      qc.invalidateQueries({ queryKey: convitesQueries.resumo(id).queryKey, exact: true }),
    ]);
}

function useLote(id: string, acao: AcaoLote) {
  const invalidar = useInvalidarPainel(id);
  return useMutation({
    mutationFn: (v: { selecao: SelecaoLote }) => painelService.lote(id, acao, v.selecao),
    onSettled: invalidar,
  });
}

export const useExcluirParticipantes = (id: string) => useLote(id, 'excluir');
export const useRestaurarParticipantes = (id: string) => useLote(id, 'restaurar');

export function useIncluirParticipantes(id: string) {
  const invalidar = useInvalidarPainel(id);
  return useMutation({
    mutationFn: (profissionalIds: number[]) => painelService.incluir(id, profissionalIds),
    onSettled: invalidar,
  });
}

/**
 * Quantas pessoas de uma seleção por filtro falam cada idioma (spec 30 R16): uma contagem por
 * idioma, com `porPagina = 1`. Um `idioma` já no filtro é interseção: fora dele conta zero.
 */
export function useIdiomasDoFiltro(id: string, filtro: Partial<FiltroLote> | null) {
  const contagens = useQueries({
    queries: IDIOMAS.map((idioma) => ({
      queryKey: [...painelQueries.all(id), 'idiomas', idioma, filtro] as const,
      queryFn: () =>
        painelService
          .listar(id, { ...filtro, idioma: [idioma], pagina: 1, porPagina: 1 })
          .then((r) => r.total),
      enabled: !!filtro && !(filtro.idioma?.length && !filtro.idioma.includes(idioma)),
    })),
  });
  return Object.fromEntries(IDIOMAS.map((i, k) => [i, contagens[k].data ?? 0])) as Record<
    Idioma,
    number
  >;
}

export function useCandidatos(id: string, busca: string) {
  return useQuery(painelQueries.candidatos(id, busca));
}

/**
 * E-mails de uma seleção (spec 31 R10): buscados ao habilitar e de novo a cada cópia, porque o
 * público pode ter mudado (R8). `aoFalhar` vê o erro antes de quem chamou (o `409`, por exemplo).
 */
export function useEmailsDaSelecao(
  id: string,
  chave: string | number,
  selecao: SelecaoLote,
  habilitado: boolean,
  aoFalhar: (e: unknown) => void,
) {
  const qc = useQueryClient();
  const queryKey = [...painelQueries.all(id), 'emails', chave] as const;
  const buscar = () =>
    painelService.emailsDaSelecao(id, selecao).catch((e: unknown) => {
      aoFalhar(e);
      throw e;
    });
  const query = useQuery({
    queryKey,
    queryFn: buscar,
    enabled: habilitado,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
  });
  const buscarDeNovo = () =>
    buscar().then((r) => {
      qc.setQueryData(queryKey, r);
      return r;
    });
  return { ...query, buscarDeNovo };
}
